import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const Products = () => {
  const { t } = useLanguage()

  const tools = [
    { name: t('products.tools.1.name'), description: t('products.tools.1.desc') },
    { name: t('products.tools.2.name'), description: t('products.tools.2.desc') },
    { name: t('products.tools.3.name'), description: t('products.tools.3.desc') },
    { name: t('products.tools.4.name'), description: t('products.tools.4.desc') },
    { name: t('products.tools.5.name'), description: t('products.tools.5.desc') },
    { name: t('products.tools.6.name'), description: t('products.tools.6.desc') },
    { name: t('products.tools.7.name'), description: t('products.tools.7.desc') },
    { name: t('products.tools.8.name'), description: t('products.tools.8.desc') },
    { name: t('products.tools.9.name'), description: t('products.tools.9.desc') }
  ]

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-products.jpg"
            placeholder="/images/placeholders/hero-products-placeholder.jpg"
            alt="Technology"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">PRODUCTS</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('products.hero.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="section-description">
              {t('products.hero.description')}
            </motion.p>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* One Soul Platform - 网格纹理背景 */}
      <section id="one-soul" className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={staggerContainer}
              viewport={{ once: true }}
            >
              <motion.div variants={staggerItem} className="section-label mb-6">{t('products.onesoul.badge')}</motion.div>
              <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
                {t('products.onesoul.title')}
              </motion.h2>
              <motion.p variants={staggerItem} className="text-gray-400 font-mono text-sm leading-relaxed mb-8">
                {t('products.onesoul.subtitle')}
              </motion.p>

              <motion.div variants={staggerItem} className="space-y-4 mb-8">
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">{t('products.onesoul.features.title')}</h3>
                {[
                  t('products.onesoul.features.1'),
                  t('products.onesoul.features.2'),
                  t('products.onesoul.features.3'),
                  t('products.onesoul.features.4')
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-white/50 text-lg">check_circle</span>
                    <span className="text-gray-400 text-sm font-mono">{feature}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div variants={staggerItem} className="space-y-4">
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">{t('products.onesoul.scenarios.title')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[t('products.onesoul.scenarios.1'), t('products.onesoul.scenarios.2'), t('products.onesoul.scenarios.3')].map((scenario, index) => (
                    <div key={index} className="p-4 border border-white/10 hover:border-white/30 transition-colors">
                      <p className="text-xs font-mono text-gray-400">{scenario}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="metal-surface p-8"
            >
              <div className="text-center">
                <span className="material-symbols-outlined text-5xl text-white/30 mb-6">memory</span>
                <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{t('products.onesoul.visual.title')}</h3>
                <p className="text-gray-500 text-sm font-mono mb-6">{t('products.onesoul.visual.subtitle')}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-4 border border-white/10">
                    <span className="font-mono text-white/70">{t('products.onesoul.visual.arch')}</span>
                    <p className="text-gray-600 text-xs mt-1 font-mono">{t('products.onesoul.visual.arch.value')}</p>
                  </div>
                  <div className="p-4 border border-white/10">
                    <span className="font-mono text-white/70">{t('products.onesoul.visual.os')}</span>
                    <p className="text-gray-600 text-xs mt-1 font-mono">{t('products.onesoul.visual.os.value')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* One Body Platform - 渐变纹理背景 */}
      <section id="one-body" className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1 metal-surface p-8"
            >
              <div className="text-center">
                <span className="material-symbols-outlined text-5xl text-white/30 mb-6">directions_car</span>
                <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{t('products.onebody.visual.title')}</h3>
                <p className="text-gray-500 text-sm font-mono mb-6">{t('products.onebody.visual.subtitle')}</p>
                <div className="space-y-4 text-sm">
                  <div className="p-4 border border-white/10">
                    <span className="font-mono text-white/70">{t('products.onebody.visual.wiring')}</span>
                    <p className="text-gray-600 text-xs mt-1 font-mono">{t('products.onebody.visual.wiring.desc')}</p>
                  </div>
                  <div className="p-4 border border-white/10">
                    <span className="font-mono text-white/70">{t('products.onebody.visual.design')}</span>
                    <p className="text-gray-600 text-xs mt-1 font-mono">{t('products.onebody.visual.design.desc')}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={staggerContainer}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <motion.div variants={staggerItem} className="section-label mb-6">{t('products.onebody.badge')}</motion.div>
              <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
                {t('products.onebody.title')}
              </motion.h2>
              <motion.p variants={staggerItem} className="text-gray-400 font-mono text-sm leading-relaxed mb-8">
                {t('products.onebody.subtitle')}
              </motion.p>

              <motion.div variants={staggerItem} className="space-y-4 mb-8">
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">{t('products.onebody.features.title')}</h3>
                {[
                  t('products.onebody.features.1'),
                  t('products.onebody.features.2'),
                  t('products.onebody.features.3')
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-white/50 text-lg">check_circle</span>
                    <span className="text-gray-400 text-sm font-mono">{feature}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Toolchain - 深灰背景 */}
      <section id="toolchain" className="relative z-10 py-24 px-6 md:px-20 bg-dark-gray">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">{t('products.toolchain.badge')}</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('products.toolchain.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('products.toolchain.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="overflow-x-auto"
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-[10px] font-mono text-gray-500 uppercase tracking-widest">{t('products.toolchain.table.name')}</th>
                  <th className="px-6 py-4 text-left text-[10px] font-mono text-gray-500 uppercase tracking-widest">{t('products.toolchain.table.description')}</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((tool, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-white/30 text-lg">build</span>
                        <span className="font-medium text-white text-sm">{tool.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm font-mono">{tool.description}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - 点阵纹理背景 */}
      <section className="relative z-10 py-32 px-6 md:px-20 bg-dot-texture">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={staggerContainer}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={textReveal} className="section-label justify-center flex mb-6">GET STARTED</motion.div>
          <motion.h2 variants={textReveal} className="text-4xl md:text-5xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
            {t('products.cta.title')}
          </motion.h2>
          <motion.p variants={textReveal} className="text-gray-500 text-lg font-mono mb-12">
            {t('products.cta.subtitle')}
          </motion.p>
          <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="section-cta group">
              <span>{t('products.cta.contact')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
              <span>{t('products.cta.demo')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}

export default Products
